/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import { uniqueId } from 'lodash';
import React, { useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { ICON_ADD_CIRCLE_OUTLINE, ICON_DRAG_INDICATOR, ICON_REMOVE_CIRCLE_OUTLINE } from '../../Icons';
import { SdsIcon } from '../SdsIcon';
import { TOOLTIP_POSITION_LEFT, TOOLTIP_POSITION_TOP } from '../Tooltip';
import { Tooltip } from '../Tooltip/Tooltip';
import { SEQUENCE_LIST_STRINGS } from './SequenceList.constants';
import { SequenceListProps } from './SequenceList.props';
import { SequenceListDots } from './elements/SequenceListDots';

export const SequenceList = <T extends Object>({
  addButtonContent,
  classes,
  defaultItemValue,
  min = 1,
  max,
  listItemContent,
  listState,
  listStateHandler,
  disableInteractivity = false,
  disableControls = false,
}: SequenceListProps<T>): JSX.Element => {
  const [isDragging, setIsDragging] = useState(false);

  const { t } = useTranslation();
  const labels = {
    addAnotherStep: t(SEQUENCE_LIST_STRINGS.addAnotherStep),
    dragHandle: t(SEQUENCE_LIST_STRINGS.dragHandle),
    dragToChangeOrder: t(SEQUENCE_LIST_STRINGS.dragToChangeOrder),
    minimumAmountOfSteps: t(SEQUENCE_LIST_STRINGS.minimumAmountOfSteps),
    pressSpaceToLift: t(SEQUENCE_LIST_STRINGS.pressSpaceToLift),
    removeStep: t(SEQUENCE_LIST_STRINGS.removeStep),
    reorder: t(SEQUENCE_LIST_STRINGS.reorder),
    twoStepsRequired: t(SEQUENCE_LIST_STRINGS.twoStepsRequired),
  };
  const containsMinimumItems = listState.length <= min;
  const addItem = (): void => {
    if (defaultItemValue) {
      listStateHandler && listStateHandler([...listState, { ...defaultItemValue, id: uniqueId() }]);
      return;
    }
    const newObj = { ...listState[0], id: uniqueId() };

    Object.keys(newObj).forEach((key) => {
      if (key !== 'id') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (newObj as any)[key] = null;
      }
    });
    listStateHandler && listStateHandler([...listState, newObj]);
  };

  const removeItem = (id: string): void => {
    listStateHandler && listStateHandler(listState.filter((item) => item.id !== id));
  };

  const onDragStart = (): void => {
    setIsDragging(true);
  };
  const onDragEnd = (result: DropResult): void => {
    setIsDragging(false);
    const { source, destination } = result;
    if (!destination) {
      return;
    }

    const items = Array.from(listState);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);
    listStateHandler && listStateHandler(items);
  };

  if (disableControls) {
    return (
      <div className={clsx('sds-sequence-list', classes)}>
        <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <ul
                {...provided.droppableProps}
                className={clsx('sds-sequence-list__list simple-list', {
                  'sds-sequence-list__list--dragging': isDragging,
                })}
                key={provided.droppableProps['data-rbd-droppable-id']}
                ref={provided.innerRef}
              >
                {listState.map((item, index) => (
                  <Draggable draggableId={item.id} index={index} key={item.id}>
                    {(provided, snapshot) => (
                      <>
                        <li
                          {...provided.draggableProps}
                          className={clsx('sds-sequence-list__item', {
                            'sds-sequence-list__item--dragging': snapshot.isDragging,
                          })}
                          ref={provided.innerRef}
                        >
                          <Tooltip label={listState.length === 1 ? labels.twoStepsRequired : labels.reorder}>
                            <div
                              {...provided.dragHandleProps}
                              aria-label={labels.dragHandle}
                              aria-roledescription={labels.pressSpaceToLift}
                              className={clsx('sds-sequence-list__item-drag-btn', {
                                'sds-sequence-list__item-drag-btn--disabled': listState.length === 1,
                              })}
                              role="button"
                              tabIndex={0}
                            >
                              <SdsIcon svg={ICON_DRAG_INDICATOR} />
                            </div>
                          </Tooltip>
                          <div className="sds-sequence-list__item-content">{listItemContent(index)}</div>
                        </li>
                      </>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  }
  if (disableInteractivity) {
    return (
      <div className={clsx('sds-sequence-list presentation-only', classes)}>
        <ul className="sds-sequence-list__list">
          {listState.map((item, index) => (
            <li className={'sds-sequence-list__item'} key={item.id}>
              <span className="sds-sequence-list__item-index-mark" data-index={index + 1}>
                {index + 1}
              </span>
              <SequenceListDots dependencies={[listState, listItemContent]} index={index} />
              <div className="sds-sequence-list__item-content">{listItemContent(index)}</div>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className={clsx('sds-sequence-list', classes)}>
      <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <ul
              {...provided.droppableProps}
              className={clsx('sds-sequence-list__list', { 'sds-sequence-list__list--dragging': isDragging })}
              key={provided.droppableProps['data-rbd-droppable-id']}
              ref={provided.innerRef}
            >
              {listState.map((item, index) => (
                <Draggable draggableId={item.id} index={index} key={item.id}>
                  {(provided, snapshot) => (
                    <>
                      <li
                        {...provided.draggableProps}
                        className={clsx('sds-sequence-list__item', {
                          'sds-sequence-list__item--dragging': snapshot.isDragging,
                        })}
                        ref={provided.innerRef}
                      >
                        <Tooltip label={listState.length === 1 ? labels.twoStepsRequired : labels.reorder}>
                          <div
                            {...provided.dragHandleProps}
                            aria-label={labels.dragHandle}
                            aria-roledescription={labels.pressSpaceToLift}
                            className={clsx('sds-sequence-list__item-drag-btn', {
                              'sds-sequence-list__item-drag-btn--disabled': listState.length === 1,
                            })}
                            role="button"
                            tabIndex={0}
                          >
                            <SdsIcon svg={ICON_DRAG_INDICATOR} />
                          </div>
                        </Tooltip>
                        <span className="sds-sequence-list__item-index-mark" data-index={index + 1}>
                          {index + 1}
                        </span>
                        <SequenceListDots dependencies={[listState, listItemContent]} index={index} />
                        <div className="sds-sequence-list__item-content">{listItemContent(index)}</div>
                        <Tooltip
                          label={
                            containsMinimumItems
                              ? labels.minimumAmountOfSteps.replace('{{count}}', min.toString())
                              : labels.removeStep
                          }
                          position={containsMinimumItems ? TOOLTIP_POSITION_LEFT : TOOLTIP_POSITION_TOP}
                        >
                          <button
                            aria-label={labels.removeStep}
                            className={clsx('sds-sequence-list__item-remove', {
                              'sds-sequence-list__item-remove--disabled': containsMinimumItems,
                            })}
                            disabled={containsMinimumItems}
                            onClick={() => removeItem(item.id)}
                          >
                            <SdsIcon svg={ICON_REMOVE_CIRCLE_OUTLINE} />
                          </button>
                        </Tooltip>
                      </li>
                    </>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
      {(!max || listState.length < max) && (
        <button className="sds-sequence-list__add-item-btn" onClick={addItem} title={labels.addAnotherStep}>
          {addButtonContent ? (
            addButtonContent
          ) : (
            <>
              <SdsIcon svg={ICON_ADD_CIRCLE_OUTLINE} />
              {labels.addAnotherStep}
            </>
          )}
        </button>
      )}
    </div>
  );
};
